/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { FilledTextFieldProps, TextFieldProps, Autocomplete, FilterOptionsState } from '@mui/material';
import type { AutocompleteProps, FormHelperTextProps } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { ITextFieldProps, TextField } from '../text-field';
import { ColorPalette } from '../config';

const useStyles = makeStyles()(() =>
  ({
    paper: {
      fontSize: '0.85rem',
      whiteSpace: 'nowrap',
    },
  })
);

// Please think carefully before adding new props to this.
export interface IMultiSelectProps<T, ClassKey extends string = string> {
  classes?: Partial<Record<ClassKey, string>>;
  label: React.ReactNode;
  value: T[] | undefined;
  onChange: (value: T[], name?: string) => void;
  getOptionLabel: (option: T) => string;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  // NOTE: this prop is not applied to the underlying Autocomplete component, although it's passed
  // in several places. If you decide to implement this, then make sure current usages are working
  // as expected.
  renderOption?: (optionProps: React.HTMLAttributes<HTMLLIElement>, option: T) => React.ReactNode;
  options: T[];
  disabled?: boolean;
  disableClearable?: true;
  error?: boolean;
  errorText?: FilledTextFieldProps['helperText'] | T;
  required?: boolean;
  className?: string;
  style?: React.CSSProperties;
  noOptionsText?: string;
  variant?: TextFieldProps['variant'];
  disableCloseOnSelect?: boolean;
  autoFocus?: boolean;
  filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[];
  getOptionDisabled?: (option: T) => boolean;
  name?: string;
  description?: string;
  renderTags?: AutocompleteProps<T, true, true, false>['renderTags'];
}

export function MultiSelect<T>(props: IMultiSelectProps<T>) {
  const {
    label,
    required,
    onChange,
    value,
    error,
    errorText,
    noOptionsText,
    variant,
    renderOption,
    isOptionEqualToValue,
    autoFocus,
    disableCloseOnSelect = true,
    filterOptions,
    getOptionDisabled,
    name,
    description,
    ...rest
  } = props;

  function handleOnChange(event: React.SyntheticEvent<Element, Event>, selected: T[]) {
    onChange && onChange(selected, name);
  }

  const { classes } = useStyles();
  const formHelperTextProps: Partial<FormHelperTextProps> = error
    ? { style: { textAlign: 'left', color: 'error' } }
    : { style: { fontSize: '0.875em', textAlign: 'right', color: ColorPalette.DisabledInputLabel } };

  return (
    <Autocomplete<T, true, true>
      {...rest}
      multiple
      value={value || []}
      onChange={handleOnChange}
      classes={{
        paper: classes.paper,
      }}
      ChipProps={{
        variant: 'outlined',
        size: 'small',
      }}
      autoHighlight
      selectOnFocus
      disableCloseOnSelect={disableCloseOnSelect}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={renderOption}
      getOptionDisabled={getOptionDisabled}
      filterOptions={filterOptions}
      renderInput={(params: ITextFieldProps) => (
        <TextField
          {...params}
          autoFocus={autoFocus}
          label={label}
          required={required}
          error={error}
          errorText={errorText}
          variant={variant}
          multiline={false}
          inputProps={{
            ...params.inputProps,
            autoComplete: 'multi-select',
          }}
          helperText={description}
          FormHelperTextProps={formHelperTextProps}
        />
      )}
      noOptionsText={noOptionsText || 'Ingen valgmuligheder'}
    />
  );
}
